/* eslint-disable prettier/prettier */
import Icon, {
    ArrowRightOutlined,
    BankOutlined,
    LeftOutlined,
    LockOutlined,
    MenuOutlined,
    RightOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { Button, Divider, Drawer, Layout, Menu, Modal, Tooltip, Row, Col } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import ReactGA from 'react-ga';

import { AccountExtension, Document, Extension, Geo } from '@tenderd/schema';
import {
    getServer,
    useAuth,
    useGeo,
    useMixpanel,
} from '@tenderd/track-web-utils';
import './SideBar.css';
import { useTranslation } from './translations/hooks';
import { SideBarTKeys } from './translations/keys';

import './globals.css';

import { PageHeader } from '@ant-design/pro-layout';
import { navigateToUrl } from 'single-spa';
import { ICustomMenuOrder, ISidebarMenuItem } from './types/sidebar';

const CustomIcon = React.memo(function CustomIcon({
    src,
    alt,
}: {
    src: string;
    alt: string;
}) {
    return (
        <Icon
            component={() => (
                <img style={{ height: 32, width: 32, filter: "invert(41%) sepia(6%) saturate(6%) hue-rotate(314deg) brightness(100%) contrast(103%)" }} src={src} alt={alt} />
            )}
        />
    );
});

const NavLink: React.FunctionComponent<{
    to: string;
    children: any;
    style?: React.CSSProperties;
    onClick?: () => any;
}> = ({ to, children, onClick, style }) => {
    return (
        <div
            style={style}
            role="button"
            tabIndex={0}
            onClick={() => {
                if (onClick) onClick();
                navigateToUrl(to);
            }}
        >
            {children}
        </div>
    );
};

export enum SideBarEvents {
    ClickedTenderdLogo = 'Clicked Tenderd Logo (Sidebar)',
    ClickedMarketplace = 'Clicked Marketplace  (Sidebar)',
    ClickedLogistics = 'Clicked Logistics (Sidebar)',
    ClickedMyOrders = 'Clicked My Orders (Sidebar)',
    ClickedTrackProjects = 'Clicked Track Projects (Sidebar)',
    ClickedSettings = 'Clicked Settings (Sidebar)',
    ClickedLoginSignUp = 'Clicked Login | Signup (Sidebar)',
    ClickedHelpAndSupport = 'Clicked Help And Support (Sidebar)',
    ClickedTendering = 'Clicked Tendering  (Sidebar)',
    ClickedMyMachines = 'Clicked My Machines  (Sidebar)',
    ClickedMyRequests = 'Clicked My Requests  (Sidebar)',
}

export enum HelpAndSupportEvents {
    ClickedPhoneNumber = 'Clicked Phone Number',
    ClickedChatSupport = 'Clicked Chat Support',
}

const { Sider } = Layout;

const useLocation = () => {
    const [path, setPath] = useState(window.location.pathname);
    const listenToPopstate = () => {
        const winPath = window.location.pathname;
        setPath(winPath);
    };
    React.useEffect(() => {
        window.addEventListener('popstate', listenToPopstate);
        return () => {
            window.removeEventListener('popstate', listenToPopstate);
        };
    }, []);
    return path;
};

const SideBar: React.FunctionComponent = (props) => {
    const url = window.location.origin;

    const { account, user } = useAuth();
    const { i18n, t } = useTranslation();
    let loc = useLocation();
    const mixpanel = useMixpanel();
    const mainGeo = useGeo();

    const [openMobileMenu, setOpenMobileMenu] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const [toggleChat, setToggleChat] = useState(false);
    const [updates, setUpdates] = useState(false);

    const [extensions, setExtensions] = useState<Document<Extension>[]>([]);
    const [installedExtensions, setInstalledExtensions] = useState<{
        [id: string]: AccountExtension;
    }>({});

    const [developer, setDeveloper] = useState(false);
    const [overrides, setOverrides] = useState(false);

    const [show404, setShow404] = useState<boolean>(false);
    const [allowedPaths, setAllowedPaths] = useState<string[]>([]);

    let active = `/${loc.split('/')[1]}`;
    let accountVerified =
        account && account.id && account.doc.internal.verified;

    const menuRef = useRef(null);

    useEffect(() => {
        const menuNode = menuRef.current;
        if (menuNode) {
            menuNode.addEventListener('scroll', handleMenuScroll);
            handleMenuScroll();
        }

        return () => {
            if (menuNode) {
                menuNode.removeEventListener('scroll', handleMenuScroll);
            }
        };
    }, []);

    const handleMenuScroll = () => {
        const menuNode = menuRef.current;
        if (menuNode) {
            const { top } = menuNode.getBoundingClientRect();
            const maxHeight = window.innerHeight - top - 200;
            menuNode.style.maxHeight = `${maxHeight}px`;
        }
    };

    const handleUpdateClose = () => {
        setUpdates(false);
        localStorage.setItem('updates', 'false');
    };

    const getExtensions = () => {
        getServer(Geo.UAE)
            .getExtensions()
            .then((docs) => {
                setExtensions(docs);
            })
            .catch((e) => {
                console.error(e.message);
            });
    };

    useEffect(() => {
        setShow404(false);
        if (
            !user ||
            !extensions ||
            extensions.length === 0 ||
            !installedExtensions ||
            Object.keys(installedExtensions).length === 0
        )
            return;

        const paths = extensions
            .filter((ext) => installedExtensions[ext.id])
            .map((ext) => ext.doc.link);
        setAllowedPaths(paths);
        paths.push('settings', 'groups', 'auth', 'bi');
        let allow = false;
        paths.forEach((path) => {
            if (loc.includes(path)) {
                allow = true;
            }
        });
        setShow404(!allow);
    }, [loc, installedExtensions, extensions, user]);

    useEffect(() => {
        if (document.querySelectorAll('.menu-shell li').length) {
            document
                .querySelectorAll('.menu-shell li')[0]
                .addEventListener('mouseover', () => {
                    setTimeout(() => {
                        try {
                            (document as any).getElementsByClassName(
                                'new-tag'
                            )[1].style.display = 'none';
                        } catch (e) { }
                    }, 250);
                });
        }
        let dev = window.localStorage.getItem('devtools');
        if (dev && dev === 'true') {
            setDeveloper(true);
        }
    }, []);

    const getInstalledExtensions = () => {
        getServer(mainGeo)
            .getInstalledExtensions({
                accountId: account.id,
            })
            .then((docs) => {
                let obj: { [id: string]: AccountExtension } = {};
                docs.forEach((doc) => {
                    let ext = doc.doc as AccountExtension;
                    if (doc.doc.users[user.doc.email]) {
                        obj[doc.id] = ext;
                    }
                });
                setInstalledExtensions(obj);
            })
            .catch((e) => {
                console.error('EXTENSIONS: ', e.message);
            });
    };

    useEffect(() => {
        if (account && account.id) {
            getInstalledExtensions();
            getExtensions();

            // Disabled since we dont have any updates since 2022, might re-enable later on if needed
            // let up = window.localStorage.getItem('updates');
            // if (!up || up === 'true') {
            //     setUpdates(true);
            // }
        }
    }, [account, mainGeo]);

    const ExtensionsMap = {
        Logistics: i18n.t(SideBarTKeys.logistics),
        Utilization: i18n.t(SideBarTKeys.utilization),
        Emissions: i18n.t(SideBarTKeys.emissions),
        Maintenance: i18n.t(SideBarTKeys.maintenance),
        Marketplace: i18n.t(SideBarTKeys.marketplace),
        Tableau: i18n.t(SideBarTKeys.tableau),
        Productivity: i18n.t(SideBarTKeys.productivity),
        Financials: i18n.t(SideBarTKeys.financials),
        'Fuel Management': i18n.t(SideBarTKeys.fuelManagement),
        'Fleet Manager': i18n.t(SideBarTKeys.fleetManager),
    };

    let trackMenuItems: ISidebarMenuItem[] = [
        ...extensions
            .filter(
                (a) =>
                    a.id === 'KmuFFwzUXIhXKIuVPJSw' ||
                    a.id === 'BFR7XDFN2iK3dzShnSxz'
            )
            .map((ext) => ({
                key: ext.doc.link,
                title: ExtensionsMap[ext.doc.short_name]
                    ? ExtensionsMap[ext.doc.short_name]
                    : ext.doc.short_name,
                label: (
                    <NavLink to={`${url}/${ext.doc.link}`}>
                        <CustomIcon
                            src={ext.doc.minimal_icon}
                            alt={ext.doc.title}
                        />
                        <span className="title">
                            {ExtensionsMap[ext.doc.short_name]
                                ? ExtensionsMap[ext.doc.short_name]
                                : ext.doc.short_name}
                        </span>
                    </NavLink>
                ),
            })),
        ...extensions
            .filter(
                (a) =>
                    installedExtensions[a.id] && a.id !== 'KmuFFwzUXIhXKIuVPJSw'
            )
            .map((ext) => ({
                key: ext.doc.link,
                title: ExtensionsMap[ext.doc.short_name]
                    ? ExtensionsMap[ext.doc.short_name]
                    : ext.doc.short_name,
                label: (
                    <NavLink to={`${url}/${ext.doc.link}`}>
                        <CustomIcon
                            src={ext.doc.minimal_icon}
                            alt={ext.doc.title}
                        />
                        <span className="title">
                            {ExtensionsMap[ext.doc.short_name]
                                ? ExtensionsMap[ext.doc.short_name]
                                : ext.doc.short_name}
                        </span>
                    </NavLink>
                ),
            })),
    ];

    // created a custom menu order for the track menu items
    const sortArrayByMenuOrder = (
        arr: ISidebarMenuItem[],
        menuOrder: ICustomMenuOrder
    ): ISidebarMenuItem[] => {
        return arr.sort((a, b) => {
            const orderA = menuOrder[a.key];
            const orderB = menuOrder[b.key];
            if (orderA === -1 && orderB === -1) return 0;
            else if (orderA === -1) return 1;
            else if (orderB === -1) return -1;
            else return (orderA || Infinity) - (orderB || Infinity);
        });
    };

    // custom menu order for the track menu items
    // if a menu item is not included in this object, it will be sorted alphabetically
    // we can change the order of the menu items by changing the number
    const trackMenuOrders: ICustomMenuOrder = {
        groups: 1,
        vision: 2,
        emissions: 3,
        safety: 4,
        bi: -1, // to keep this menu item at the end of the list, set the order to -1
    };

    // sort the track menu items by the custom menu order
    trackMenuItems = useMemo(
        () => sortArrayByMenuOrder(trackMenuItems, trackMenuOrders),
        [trackMenuItems]
    );

    useEffect(() => {
        const salesiq = (window as any).$zoho?.salesiq;
        if (!salesiq) return;
        if (toggleChat) {
            const toggledVisibility =
                salesiq.values.floatvisible === 'show' ? 'hide' : 'show';
            salesiq.floatbutton.visible(toggledVisibility);
            salesiq.floatwindow.visible(toggledVisibility);
            setToggleChat(false);
        }
    }, [toggleChat, (window as any).$zoho?.salesiq]);

    useEffect(() => {
        const salesiq = (window as any).$zoho?.salesiq;
        if (salesiq && user?.doc) {
            const salesiq = (window as any).$zoho.salesiq;
            salesiq.visitor.email(user.doc.email);
            salesiq.visitor.name(user.doc.name);
            salesiq.visitor.contactnumber(user.doc.phone_number);
        }
    }, [(window as any).$zoho?.salesiq, user?.doc]);

    const onCollapse = () => {
        setCollapsed(!collapsed);
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 300);
    };

    let logo = (
        <img
            className="logo-img"
            src="https://tenderd-ui.storage.googleapis.com/assets/logo-square.svg"
            alt="Tenderd Track"
        />
    );
    if (account && account.doc && account.doc.logo_url) {
        logo = (
            <img
                className="logo-img-external"
                src={account.doc.logo_url}
                alt="Tenderd Track"
            />
        );
    }

    let logo_large = (
        <img
            className="logo-img"
            src="https://storage.googleapis.com/tenderd-ui/assets/logo.png"
            alt="Tenderd Track"
        />
    );
    if (account && account.doc && account.doc.logo_large_url) {
        logo_large = (
            <img
                className="logo-img-external"
                src={account.doc.logo_large_url}
                alt="Tenderd Track"
            />
        );
    }

    return (
        <>
            <Modal
                width={800}
                footer={<Button onClick={handleUpdateClose}>Close</Button>}
                bodyStyle={{ maxHeight: 550, overflowY: 'auto' }}
                closable={false}
                title="Platform update - January 2022"
                open={updates}
                onOk={handleUpdateClose}
                onCancel={handleUpdateClose}
            >
                <h2 style={{ fontWeight: '700', marginBottom: 0 }}>
                    Reports have moved
                </h2>
                <p>
                    The reports section in track has now moved to its own
                    extension - BI & reports
                </p>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <div style={{ flexGrow: 1, textAlign: 'center' }}>
                        <img
                            src={require('./assets/before.png')}
                            style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: 6,
                                border: '0.5px solid rgb(229, 232, 235)',
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                                marginBottom: 10,
                            }}
                            alt="Track reports"
                        />
                        Before
                    </div>

                    <ArrowRightOutlined
                        style={{
                            marginLeft: 10,
                            marginRight: 10,
                            fontSize: 28,
                        }}
                    />
                    <div style={{ flexGrow: 1, textAlign: 'center' }}>
                        <img
                            src={require('./assets/after.png')}
                            style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: 6,
                                border: '0.5px solid rgb(229, 232, 235)',
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                                marginBottom: 10,
                            }}
                            alt="Track reports"
                        />
                        After
                    </div>
                </div>
                <Divider />
                <h2 style={{ fontWeight: '700', marginBottom: 0 }}>
                    Custom report builder
                </h2>
                <p>
                    You can now customise existing reports or create a
                    completely new report with our APIs
                </p>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <div style={{ flexGrow: 1, textAlign: 'center' }}>
                        <img
                            src={require('./assets/update2.png')}
                            style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: 6,
                                border: '0.5px solid rgb(229, 232, 235)',
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                                marginBottom: 10,
                            }}
                            alt="Track reports"
                        />
                    </div>
                    <div
                        style={{
                            marginLeft: 20,
                            flexGrow: 1,
                            textAlign: 'center',
                        }}
                    >
                        <img
                            src={require('./assets/update1.png')}
                            style={{
                                width: '100%',
                                display: 'block',
                                borderRadius: 6,
                                border: '0.5px solid rgb(229, 232, 235)',
                                boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 2px',
                                marginBottom: 10,
                            }}
                            alt="Track reports"
                        />
                    </div>
                </div>
            </Modal>
            <Sider
                className={'sidebar'}
                collapsed={collapsed}
                onCollapse={() => {
                    if (user) onCollapse();
                }}
                width={220}
            >
                <div className="logo">
                    <a
                        href={`https://track.tenderd.com`}
                        onClick={() => {
                            ReactGA.event({
                                category: SideBarEvents.ClickedTenderdLogo,
                                action: 'Track WebApp',
                            });
                            mixpanel.track(SideBarEvents.ClickedTenderdLogo);
                        }}
                    >
                        {collapsed ? logo : logo_large}
                    </a>
                </div>
                {/* <div ref={menuRef} style={{ overflowY: 'scroll' }}> */}
                <div className="sidebar-menus">
                    <Menu
                        mode="vertical"
                        selectedKeys={[active.replace('/', '')]}
                        items={trackMenuItems}
                    />
                </div>
                <div className="ant-layout-sider-trigger" style={{ width: "100%", cursor: "pointer" }} onClick={() => {
                    if (user) onCollapse();
                }}>
                    <span role="img" aria-label="right" className="anticon anticon-right">
                        {collapsed ? <RightOutlined /> : <LeftOutlined />}
                    </span>
                </div>
                <Divider style={{ margin: "5px 0" }} />
                <div style={{ padding: 10 }}>
                    {accountVerified ? (
                        <Tooltip
                            placement="right"
                            overlayStyle={{ width: '100%' }}
                            title={
                                <>
                                    <Row gutter={10}>
                                        <Col><UserOutlined /></Col>
                                        <Col>
                                            {user.doc.name}{' '}
                                        </Col>
                                    </Row>
                                    <Row gutter={10}>
                                        <Col><BankOutlined /></Col>
                                        <Col>
                                            {account.doc.name}
                                        </Col>
                                    </Row>
                                    <Divider style={{ margin: "5px 0" }} className="account divider" />
                                    <Row gutter={10}>
                                        <Col><LockOutlined /></Col>
                                        <Col>
                                            <b>
                                                {i18n.t(
                                                    SideBarTKeys.bronzeMembership
                                                )}
                                            </b>
                                        </Col>
                                    </Row>
                                </>
                            }
                        >
                            <Button
                                type="primary"
                                block
                                className="verified-butt"
                                size="small"
                            >
                                <img
                                    width={16}
                                    height={16}
                                    src="https://tenderd-ui.storage.googleapis.com/assets/checkmark.png"
                                    alt="Verified"
                                />
                                {!collapsed ? (
                                    <span className="status-padding">
                                        {i18n.t(SideBarTKeys.verifiedAccount)}
                                    </span>
                                ) : (
                                    ''
                                )}
                            </Button>
                        </Tooltip>
                    ) : (
                        <Button
                            type="primary"
                            block
                            className="verified-butt unverified"
                            size="small"
                        >
                            <NavLink to={`${url}/settings`}>
                                <img
                                    height={16}
                                    width={16}
                                    src="https://tenderd-ui.storage.googleapis.com/assets/checkmark.png"
                                    alt="Verified"
                                />
                                {!collapsed ? (
                                    <span className="status-padding">
                                        {i18n.t(SideBarTKeys.unverifiedAccount)}
                                    </span>
                                ) : (
                                    ''
                                )}
                            </NavLink>
                        </Button>
                    )}
                </div>
            </Sider>
            {isMobile && (
                <>
                    <Drawer
                        width={window.innerWidth - 100}
                        destroyOnClose={true}
                        title={logo_large}
                        className={'sidebar-mobile-drawer'}
                        placement="left"
                        closable={false}
                        maskClosable={true}
                        open={openMobileMenu}
                        onClose={() => {
                            setOpenMobileMenu(false);
                        }}
                        getContainer={false}
                    >
                        <Menu
                            mode="vertical"
                            selectedKeys={[active.replace('/', '')]}
                            className="menu-shell"
                            items={trackMenuItems}
                        />
                    </Drawer>
                    <PageHeader
                        className={'sidebar-page-header'}
                        ghost={false}
                        title={
                            <a
                                href={`https://track.tenderd.com`}
                                onClick={() => {
                                    ReactGA.event({
                                        category:
                                            SideBarEvents.ClickedTenderdLogo,
                                        action: 'Track WebApp',
                                    });
                                    mixpanel.track(
                                        SideBarEvents.ClickedTenderdLogo
                                    );
                                }}
                            >
                                {logo_large}
                            </a>
                        }
                        onBack={() => setOpenMobileMenu(true)}
                        backIcon={<MenuOutlined />}
                    />
                </>
            )}
            {show404 && (
                <div className="unauthorized-container">
                    <div className="card">
                        <div className="left">
                            <img
                                src="https://storage.googleapis.com/tenderd-ui/assets/Oops!%20404%20Error%20with%20a%20broken%20robot.gif"
                                alt="404"
                            />
                        </div>
                        <div className="right">
                            <h1>{i18n.t(SideBarTKeys.notAllowed)}</h1>
                            <p>{i18n.t(SideBarTKeys.notAllowedDesc)}</p>
                            <h3>
                                {i18n.t(SideBarTKeys.installedExtensions)} :
                            </h3>
                            <div className="allowed">
                                {extensions.map((ext) => {
                                    if (allowedPaths.includes(ext.doc.link)) {
                                        return (
                                            <Button
                                                className="allow-btn"
                                                type="dashed"
                                                href={`${url}/${ext.doc.link}`}
                                            >
                                                {ext.doc.short_name}
                                            </Button>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SideBar;
